import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  HiOutlineChatAlt2,
  HiOutlineX,
  HiOutlineThumbUp,
  HiOutlineExclamationCircle,
  HiOutlineExclamation,
  HiOutlineAnnotation,
  HiOutlineCheck,
  HiOutlinePaperAirplane,
} from 'react-icons/hi';
import { useAuth } from '../../context/AuthContext';
import posSyncPolicy from '../../utils/posSyncPolicy';
import api from '../../utils/api';

/**
 * "Give Feedback" flyout — the early-adopter → owner channel.
 *
 * Deliberately not a page or a modal: it borrows AIChat's right-edge geometry so
 * the two panels read as siblings, and so feedback can be written WITHOUT losing
 * the screen it is about. Almost none of AIChat's complexity comes with it.
 *
 * The submission is stamped server-side with the tenant and the user — nothing
 * identifying is sent from here, and nothing needs to be. See
 * server/routes/feedback.js.
 */

const MAX_MESSAGE = 4000;        // matches platform_feedback.message NVARCHAR(4000)
const COUNTER_WARN_AT = 3800;
const MIN_MESSAGE = 5;           // matches the server-side validator

const FEEDBACK_TYPES = [
  {
    value: 'applause',
    label: 'Applause',
    Icon: HiOutlineThumbUp,
    hint: 'Something that works well',
    placeholder: 'What worked well? Knowing what to protect is as useful as knowing what to fix.',
    selected: 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/60',
  },
  {
    value: 'issue',
    label: 'Issue',
    Icon: HiOutlineExclamationCircle,
    hint: 'Awkward or slow',
    placeholder: 'What felt awkward, slow or confusing? Where were you trying to get to?',
    selected: 'border-amber-500 bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300 dark:border-amber-500/60',
  },
  {
    value: 'bug',
    label: 'Bug',
    // Heroicons v1 has no bug glyph. Triangle vs. circle reads as bug-vs-issue.
    Icon: HiOutlineExclamation,
    hint: 'Something is broken',
    placeholder: 'What went wrong, and what were you doing just before it happened?',
    selected: 'border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-500/10 dark:text-rose-300 dark:border-rose-500/60',
  },
  {
    value: 'comment',
    label: 'Comment',
    Icon: HiOutlineAnnotation,
    hint: 'Anything else',
    placeholder: 'Anything at all — an idea, a question, something you wish this did.',
    selected: 'border-slate-500 bg-slate-100 text-slate-700 dark:bg-slate-500/10 dark:text-slate-200 dark:border-slate-500/60',
  },
];

// Tenant + user: user ids repeat across tenant databases, so a user-only key
// could restore a half-written report from one company into another company's
// feedback box — free text that routinely names customers and figures.
const draftKey = (userId) => {
  let tenant = 'default';
  try { tenant = posSyncPolicy.tenantFromToken(localStorage.getItem('token')) || 'default'; } catch { /* ignore */ }
  return `journey.feedback.draft.${tenant}.${userId || 'anon'}`;
};

const FeedbackPanel = ({ onClose, currentPage = '/' }) => {
  const { user } = useAuth();

  const [type, setType] = useState('comment');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sentId, setSentId] = useState(null);
  const textareaRef = useRef(null);

  const activeType = FEEDBACK_TYPES.find((t) => t.value === type) || FEEDBACK_TYPES[3];

  // Restore any draft, then focus. Losing three paragraphs of a bug report to a
  // stray Escape is how you stop receiving bug reports.
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(draftKey(user?.id)) || 'null');
      if (saved?.message) setMessage(String(saved.message).slice(0, MAX_MESSAGE));
      if (saved?.type && FEEDBACK_TYPES.some((t) => t.value === saved.type)) setType(saved.type);
    } catch { /* a corrupt draft is not worth surfacing */ }
    textareaRef.current?.focus();
  }, [user?.id]);

  useEffect(() => {
    if (sentId) return;
    try {
      if (message.trim()) {
        localStorage.setItem(draftKey(user?.id), JSON.stringify({ message, type }));
      } else {
        localStorage.removeItem(draftKey(user?.id));
      }
    } catch { /* private mode / quota — the draft is a convenience, not a feature */ }
  }, [message, type, sentId, user?.id]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const submit = useCallback(async () => {
    if (submitting || message.trim().length < MIN_MESSAGE) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post('/feedback', {
        feedback_type: type,
        message: message.trim(),
        page_path: currentPage,
      });
      try { localStorage.removeItem(draftKey(user?.id)); } catch { /* see above */ }
      setSentId(res.data?.id ?? true);
    } catch (err) {
      setError(
        err.response?.data?.message
        || 'We could not send that just now. Your text is still here — please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  }, [submitting, message, type, currentPage, user?.id]);

  const startAnother = () => {
    setSentId(null);
    setMessage('');
    setType('comment');
    setError(null);
    // Defer: the textarea does not exist until the success state unmounts.
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const tooShort = message.trim().length < MIN_MESSAGE;

  return (
    <div
      className="fixed z-50 animate-[feedbackPanelIn_180ms_ease-out]"
      // Mirrors AIChat's collapsed geometry so the two panels line up on the
      // right edge. 26rem rather than its 29rem — this holds a picker and a
      // textarea, not a conversation. The max(..., 1rem) guard is copied
      // verbatim: it is what keeps the panel usable on a narrow phone.
      style={{ top: 0, right: 0, bottom: 0, left: 'max(calc(100vw - 26rem), 1rem)' }}
      role="dialog"
      aria-label="Give feedback"
    >
      <div className="relative h-full w-full flex flex-col overflow-hidden rounded-bl-[24px] rounded-br-[24px] border-l border-gray-200 bg-white shadow-[0_30px_90px_rgba(15,23,42,0.18)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_30px_90px_rgba(2,6,23,0.55)]">
        {/* The same 3px gradient hairline AIChat wears, so the two panels are
            recognisably the same kind of surface. */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-gradient-to-r from-sky-400 via-emerald-500 to-amber-400 dark:from-cyan-300 dark:via-emerald-400 dark:to-amber-300" />

        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-slate-800">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-8 h-8 rounded-xl flex items-center justify-center bg-emerald-50 text-emerald-600 border border-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:border-emerald-500/30">
              <HiOutlineChatAlt2 className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold text-[15px] leading-4 text-navy-900 dark:text-slate-100">Send Feedback</p>
              <p className="text-[11px] mt-0.5 text-gray-500 dark:text-slate-400">Help shape Journey.AI</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Close"
            aria-label="Close feedback"
            className="p-1 rounded transition-colors hover:bg-gray-100 text-gray-500 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            <HiOutlineX className="w-5 h-5" />
          </button>
        </div>

        {sentId ? (
          /* Success replaces the body and does NOT auto-dismiss — a confirmation
             that vanishes before it is read is worse than none. */
          <div className="flex-1 flex flex-col items-center justify-center text-center px-8">
            <div className="w-14 h-14 rounded-full flex items-center justify-center bg-emerald-100 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300">
              <HiOutlineCheck className="w-7 h-7" />
            </div>
            <p className="mt-4 font-semibold text-gray-900 dark:text-slate-100">Thank you</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-slate-400">
              This goes straight to the Journey.AI team, and we do read all of it.
            </p>
            <div className="mt-6 flex items-center gap-2">
              <button
                type="button"
                onClick={startAnother}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 transition-colors dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
              >
                Send another
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-3 py-2 text-sm rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {/* Type picker — a real radio group, not four buttons, so it is
                  navigable by keyboard the way its shape promises. */}
              <div>
                <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mb-2">What kind of feedback?</p>
                <div className="grid grid-cols-2 gap-2" role="radiogroup" aria-label="Feedback type">
                  {FEEDBACK_TYPES.map(({ value, label, Icon, hint, selected }) => {
                    const isActive = type === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        role="radio"
                        aria-checked={isActive}
                        onClick={() => setType(value)}
                        className={`flex flex-col items-start gap-1 p-3 rounded-xl border text-left transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400 ${
                          isActive
                            ? selected
                            : 'border-gray-200 text-gray-600 hover:bg-gray-50 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800'
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                        <span className="text-sm font-medium">{label}</span>
                        <span className="text-[11px] opacity-70 leading-tight">{hint}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Message */}
              <div>
                <label htmlFor="feedback-message" className="text-xs font-medium text-gray-500 dark:text-slate-400">
                  Tell us more
                </label>
                <textarea
                  id="feedback-message"
                  ref={textareaRef}
                  rows={9}
                  maxLength={MAX_MESSAGE}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={activeType.placeholder}
                  className="mt-1.5 w-full px-3 py-2 text-sm rounded-xl border border-gray-200 bg-white text-gray-900 placeholder-gray-400 resize-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
                />
                <div className="mt-1 flex items-center justify-between text-[11px]">
                  <span className="text-gray-400 dark:text-slate-500">
                    {tooShort && message.length > 0 ? 'A little more detail, please.' : ''}
                  </span>
                  <span className={message.length >= COUNTER_WARN_AT ? 'text-amber-600 dark:text-amber-400' : 'text-gray-400 dark:text-slate-500'}>
                    {message.length}/{MAX_MESSAGE}
                  </span>
                </div>
              </div>

              {/* Context + disclosure. The second line is the whole trust
                  argument for this feature, so it is not optional. */}
              <div className="rounded-xl border border-gray-200 bg-gray-50 px-3 py-2.5 dark:border-slate-800 dark:bg-slate-800/50">
                <div className="flex items-center gap-2 text-[11px] text-gray-600 dark:text-slate-300">
                  <span className="font-medium">Page</span>
                  <code className="px-1.5 py-0.5 rounded bg-white border border-gray-200 truncate dark:bg-slate-900 dark:border-slate-700">
                    {currentPage}
                  </code>
                </div>
                <p className="mt-1.5 text-[11px] text-gray-500 dark:text-slate-400">
                  Sent with your name, email, company and the page you are on, so we can follow up.
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-gray-200 p-4 dark:border-slate-800">
              {error && (
                <p className="mb-2 text-[12px] text-rose-600 dark:text-rose-400">{error}</p>
              )}
              <button
                type="button"
                onClick={submit}
                disabled={submitting || tooShort}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 transition-all shadow-md shadow-emerald-500/25 disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-1 dark:focus:ring-offset-slate-900"
              >
                <HiOutlinePaperAirplane className="w-4 h-4" />
                {submitting ? 'Sending…' : 'Send feedback'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Named feedbackPanelIn, not aiPanelIn: both panels can be mounted in the
          same document and a shared keyframe name would collide silently. */}
      <style>{`
        @keyframes feedbackPanelIn {
          from { opacity: 0; transform: translateX(10px); }
          to   { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default FeedbackPanel;
